<template>
  <LoadingSpinner />
  <SupervisorMode v-if="supervisorModeStarted || multiAccountModeStarted" :id="supervisedEtabId" :name="etabName" />
  <AppDesktopLayout v-if="breakpointSize">
    <component :is="pageLayoutComponent" />
  </AppDesktopLayout>
  <AppMobileLayout v-else>
    <component :is="pageLayoutComponent" />
  </AppMobileLayout>
  <CookieBanner v-if="showCookieBanner" @hide-banner="showCookieBanner = false" />
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeUnmount } from "vue";
  import { useQuasar } from "quasar";
  import { useRoute } from "vue-router";
  import { posthog } from "posthog-js";

  // Imports des managers, services et helpers
  import { CORE_ACTION_FEATURE_TOGGLES_FETCH_PRIVATE, CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE } from "@core/store/types";
  import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
  import { RouterHelpers } from "@core/helpers";
  import { SplashScreen } from "@capacitor/splash-screen";
  import { STARTUP_DELAY, META_LAYOUT, CORE_EVENTS, API_NAMESPACE_ETAB, FEATURE_TOGGLES } from "@core/constants";
  import { ETAB_LAYOUTS } from "@etab/config";
  import { layoutComponents } from "@etab/layouts";
  import { MultiAccountManager, SupervisorManager } from "@etab/managers";
  import { EtabProfileService } from "@core/services";
  import { BatchManager } from "@core/plugins/batch/batch.manager";
  import { AuthenticationManager } from "@core/managers/authentication.manager";
  import { EventManager } from "@core/managers/event.manager";
  import { COOKIE_CONSENT_EVENTS } from "@core/managers/cookie-consent.manager";
  import { Logger } from "@core/helpers/logger.helpers";

  // Import des composants
  import AppDesktopLayout from "@etab/layouts/desktop/AppDesktop.layout.vue";
  import AppMobileLayout from "@etab/layouts/mobile/AppMobile.layout.vue";
  import SupervisorMode from "@etab/components/common/SupervisorMode.functionnal.vue";
  import CookieBanner from "@core/components/common/CookieBanner.component.vue";

  // State
  const etabName = ref("");
  const etabId = ref(null);
  const userIsAuthenticated = ref(false);
  const showCookieBanner = ref(false);
  let timer = null;

  // Composables
  const $q = useQuasar();
  const route = useRoute();
  const featureTogglesStore = useFeatureTogglesStore();

  // Computed
  const pageLayoutComponent = computed(() => RouterHelpers.getRouteMeta(route, META_LAYOUT) || ETAB_LAYOUTS.default);

  const supervisorModeStarted = computed(() => SupervisorManager.hasStarted);
  const supervisedEtabId = computed(() => SupervisorManager.supervisedEtabId);
  const breakpointSize = computed(() => $q.screen.gt.xs);
  const multiAccountModeStarted = computed(() => MultiAccountManager.hasStarted);
  const posthogEnabled = computed(
    () =>
      featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_ONBOARDING) ||
      featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_TEMPLATE_FUNNEL),
  );

  // Méthodes
  const removePrestartupStyle = () => {
    document.body.className = document.body.className.replace("app-starting", "");
  };

  const getEtabId = async () => {
    try {
      etabId.value = await EtabProfileService.getEtabId();
    } catch (error) {
      throw error;
    }
  };

  const getSupervisedEtabName = async () => {
    try {
      if (multiAccountModeStarted.value) {
        etabName.value = await EtabProfileService.getEtabName();
      }
    } catch (error) {
      Logger.error("Error getting supervised etab name:", error);
    }
  };

  const init = async () => {
    try {
      userIsAuthenticated.value = await AuthenticationManager.isUserAuthenticated();

      if (userIsAuthenticated.value) {
        await getEtabId();
        await getSupervisedEtabName();
      }

      setupEventListeners();
    } catch (error) {
      Logger.error("Init failed:", error);
    }
  };

  const setupEventListeners = () => {
    EventManager.on(CORE_EVENTS.ON_LOGIN, async () => {
      try {
        await featureTogglesStore[CORE_ACTION_FEATURE_TOGGLES_FETCH_PRIVATE](API_NAMESPACE_ETAB);
        await getEtabId();
      } catch (error) {
        Logger.error("Error in login handler:", error);
      }
    });

    EventManager.on(CORE_EVENTS.ON_LOGOUT, () => {
      if (posthog) posthog.reset();
    });
  };

  // Lifecycle Hooks
  onMounted(async () => {
    EventManager.on(COOKIE_CONSENT_EVENTS.SHOW_BANNER, () => {
      showCookieBanner.value = true;
    });
    timer = setTimeout(() => {
      removePrestartupStyle();
      SplashScreen.hide();
      if (userIsAuthenticated.value) {
        BatchManager.disableDoNotDisturb();
      }
    }, STARTUP_DELAY);
    await init();
  });

  onBeforeUnmount(() => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    EventManager.off(CORE_EVENTS.ON_LOGIN);
    EventManager.off(CORE_EVENTS.ON_LOGOUT);
  });

  // Components registration
  defineOptions({
    components: {
      AppDesktopLayout,
      AppMobileLayout,
      SupervisorMode,
      ...layoutComponents,
    },
  });
</script>
